import type { Item, Person, PersonId } from '../types.js';
import type { ResolveResult } from './resolve.js';

/**
 * Say back, in words, what an assignment means.
 *
 * Generated from the resolved assignment, not by the model, so the readback
 * can never disagree with the numbers. Items are grouped by who holds them so
 * "Alex and you: Beer, Wine · everyone: Ribs, Steak" is one line, not eight.
 */
export interface ReadbackGroup {
  /** Sorted person ids; empty means unclaimed. */
  holders: PersonId[];
  /** "you and Alex", "everyone", "Ana", "nobody yet" */
  who: string;
  items: string[];
}

export function joinNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1) ?? ''}`;
}

export function describeAssignments(
  resolved: ResolveResult,
  items: Item[],
  people: Person[],
  speakerId?: PersonId,
): ReadbackGroup[] {
  const nameOf = (id: PersonId): string =>
    id === speakerId ? 'you' : (people.find((person) => person.id === id)?.name ?? id);
  const groups = new Map<string, ReadbackGroup>();

  for (const item of items) {
    const shares = resolved.assignments.get(item.id) ?? [];
    const holders = shares.map((share) => share.personId).sort();
    const uneven = shares.some((share) => share.weight !== 1);
    const key = `${holders.join(',')}${uneven ? '|w' : ''}`;

    let group = groups.get(key);
    if (!group) {
      let who: string;
      if (holders.length === 0) who = 'nobody yet';
      else if (holders.length === people.length && people.length > 1) who = 'everyone';
      else {
        // Speaker first, then roster order, so "you and Alex" reads naturally.
        const ordered = [
          ...holders.filter((id) => id === speakerId),
          ...people.filter((person) => person.id !== speakerId && holders.includes(person.id)).map((person) => person.id),
        ];
        who = joinNames(ordered.map(nameOf));
        if (uneven) who += ' (uneven)';
      }
      group = { holders, who, items: [] };
      groups.set(key, group);
    }
    group.items.push(item.name);
  }

  // Claimed groups first, unclaimed last, so the open question ends the readback.
  return [...groups.values()].sort((a, b) => Number(a.holders.length === 0) - Number(b.holders.length === 0));
}

/** One-line readback: "you and Alex: Beer, Wine · everyone: Ribs · nobody yet: Coke". */
export function readback(groups: ReadbackGroup[]): string {
  return groups.map((group) => `${group.who}: ${group.items.join(', ')}`).join(' · ');
}
